import * as React from 'react';
import { houseImg1, houseImg2, houseImg3, houseImg4 } from '../../assets';
import fundStructureIcon from '../../assets/icons/fund-structure.svg';
import tokenizeIcon from '../../assets/icons/tokenize.svg';
import distributeIcon from '../../assets/icons/distribute.svg';
import manageIcon from '../../assets/icons/manage.svg';
import viewInvestmentIcon from '../../assets/icons/view-investment.svg';
import onboardingIcon from '../../assets/icons/onboarding.svg';
import investIcon from '../../assets/icons/invest.svg';
import portfolioIcon from '../../assets/icons/portfolio.svg';

export interface StepProps {
  number: string;
  title: string;
  description: string;
  imageSrc: string;
  imageAlt: string;
}

export const HowItWorks: React.FC = () => {
  const [activeTab, setActiveTab] = React.useState<'seller' | 'buyer'>('seller');

  const selletSteps: StepProps[] = [
    {
      number: '01',
      title: 'Structure',
      description: 'Configure your fund tokenization strategy with our compliance-first approach and expert guidance.',
      imageSrc: fundStructureIcon,
      imageAlt: 'Fund structure diagram',
    },
    {
      number: '02',
      title: 'Tokenize',
      description: 'Deploy your fund on blockchain with automated compliance, KYC/AML, and investor onboarding.',
      imageSrc: tokenizeIcon,
      imageAlt: 'Tokenization process',
    },
    {
      number: '03',
      title: 'Distribute',
      description: 'Offer tokens to investors globally with programmable compliance and real-time settlement.',
      imageSrc: distributeIcon,
      imageAlt: 'Global token distribution',
    },
    {
      number: '04',
      title: 'Manage',
      description: 'Access comprehensive fund administration tools, automated reporting, and liquidity options.',
      imageSrc: manageIcon,
      imageAlt: 'Fund management dashboard',
    },
  ];

  const buyerSteps: StepProps[] = [
    {
      number: '01',
      title: 'View Investment Offer',
      description: 'Access fund opportunity via Equitee Edge secure portal',
      imageSrc: viewInvestmentIcon,
      imageAlt: 'View investment offer illustration',
    },
    {
      number: '02',
      title: 'Complete Onboarding',
      description: 'Complete KYC/AML and verify investor accreditation',
      imageSrc: onboardingIcon,
      imageAlt: 'Onboarding process illustration',
    },
    {
      number: '03',
      title: 'Initiate Investment',
      description: 'Connect wallet and complete the purchase with fiat or crypto',
      imageSrc: investIcon,
      imageAlt: 'Payment initiation illustration',
    },
    {
      number: '04',
      title: 'Manage Investment',
      description: 'Access your dashboard to track performance and manage your portfolio',
      imageSrc: portfolioIcon,
      imageAlt: 'Portfolio management illustration',
    },
  ];

  const steps = activeTab === 'seller' ? selletSteps : buyerSteps;

  const Step: React.FC<StepProps> = ({ number, title, description, imageSrc, imageAlt }) => {
    return (
      <div className="flex flex-wrap flex-1 shrink gap-6 items-center basis-0 min-w-[240px] max-md:max-w-full bg-white p-6 rounded-lg shadow-sm hover:shadow-md transition-all duration-300 border border-gray-100">
        <div className="flex flex-col flex-1 shrink self-stretch my-auto basis-0 min-w-[240px] max-md:max-w-full">
          <div className="flex items-center mb-4">
            <span className="bg-[#6741c6] text-white text-xl font-bold w-10 h-10 rounded-full flex items-center justify-center mr-4">
              {number}
            </span>
            <h4 className="text-2xl font-semibold leading-none text-[#333333]">
              {title}
            </h4>
          </div>
          <p className="mt-2 text-lg leading-tight text-gray-600">
            {description}
          </p>
        </div>
        <div className="bg-white p-0 rounded-lg flex items-center justify-center min-w-[240px] w-[242px]">
          <img
            loading="lazy"
            src={imageSrc}
            alt={imageAlt}
            className="object-contain h-[160px] w-[160px]"
          />
        </div>
      </div>
    );
  };

  return (
    <section id="how-it-works" className="flex flex-col pt-16 mt-8 scroll-mt-24">
      <h2 className="text-5xl font-extrabold tracking-tight leading-none text-slate-900 max-md:max-w-full max-md:text-4xl">
        How it Works?
      </h2>

      <div className="flex flex-col mt-10 w-full max-md:max-w-full">
        <nav className="flex self-start p-1.5 text-base font-medium whitespace-nowrap rounded-lg bg-slate-100 min-h-[56px] mb-8">
          <button
            onClick={() => setActiveTab('seller')}
            className={`gap-2.5 self-stretch px-6 py-2 transition-all duration-300 rounded-md ${
              activeTab === 'seller' 
                ? 'bg-[#6741c6] text-white font-medium' 
                : 'text-slate-700 hover:bg-[#e9e3f5] hover:text-[#6741c6]'
            }`}
          >
            Fund Manager
          </button>
          <button
            onClick={() => setActiveTab('buyer')}
            className={`gap-2.5 self-stretch px-6 py-2 ml-2 transition-all duration-300 rounded-md ${
              activeTab === 'buyer' 
                ? 'bg-[#6741c6] text-white font-medium' 
                : 'text-slate-700 hover:bg-[#e9e3f5] hover:text-[#6741c6]'
            }`}
          >
            Investor
          </button>
        </nav>

        <div className="flex flex-col mt-4 w-full max-md:max-w-full">
          <div className="flex flex-wrap gap-10 items-start w-full max-md:max-w-full">
            {steps.slice(0, 2).map((step) => (
              <Step key={step.number} {...step} />
            ))}
          </div>
          <div className="flex flex-wrap gap-10 items-start mt-10 w-full max-md:max-w-full">
            {steps.slice(2, 4).map((step) => (
              <Step key={step.number} {...step} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
